import React, { useState, useRef } from "react";
import { C } from "../lib/colors.js";
import { ui } from "../lib/telemetry/instrument.js";

// Shared drag machinery for the track+knob sliders used by both
// AUTO-REFORECAST and ALERT THRESHOLD in the Tracking Settings flyover.
// Two ways to set a value:
//   - Click any tick label in the row underneath (snap to that index)
//   - Click + drag anywhere along the track (snaps to nearest index
//     as the pointer moves). Pointer capture on mousedown keeps the
//     drag alive even when the cursor leaves the track horizontally.
// The track's hit area is taller than the visible 4px bar (14px total)
// so it's easy to grab. Visible track + knob are pointer-event-disabled
// so the wrapper captures every event uniformly.
// `pickValueFromClientX(clientX, rect)` does the pick-math for the specific
// slider. Whether a pick COMMITS as it moves or once on release is the caller's
// choice: pass `onDragEnd` to be told when the drag ends and commit there.
function useDragSlider(pickValueFromClientX, onDragEnd) {
  const trackRef = useRef(null);
  // `dragging` drives the CSS transition (must re-render to toggle it), so it's
  // state — not a ref. The ref mirrors it for move-gating inside onPointerMove,
  // where reading state would risk a stale closure.
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const pickFromClient = (clientX) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    pickValueFromClientX(clientX, rect);
  };
  const onPointerDown = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    setDragging(true);
    if (e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {
        /* unsupported / already captured */
      }
    }
    pickFromClient(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    pickFromClient(e.clientX);
  };
  const endDrag = (e) => {
    const wasDragging = draggingRef.current;
    draggingRef.current = false;
    setDragging(false);
    if (e.currentTarget && e.currentTarget.releasePointerCapture) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* fine */
      }
    }
    // After the capture is released, so a commit that re-renders cannot race it.
    // Gated on a live drag so a stray pointerup (one that never went through
    // onPointerDown, e.g. a release that began outside the track) commits nothing.
    // A plain click DOES pass through here, and should: onPointerDown starts the
    // drag and picks, and this is what turns that into the single write.
    if (wasDragging && onDragEnd) onDragEnd();
  };
  // Suppress the knob/fill width transitions while actively dragging
  // so the knob tracks the cursor 1:1 instead of easing behind it.
  const trans = dragging ? "none" : "left 0.15s ease, width 0.15s ease";
  return { trackRef, onPointerDown, onPointerMove, endDrag, trans };
}

// Slider control used by both AUTO-REFORECAST and ALERT THRESHOLD in
// the Tracking Settings flyover. Index-based — `opts[idx]` is the
// current value.
export function TrackingSlider({
  opts,
  idx,
  fmt,
  onPick,
  fill = C.purpleDim,
  accent = C.purpleL,
  "data-ui": uiId,
  uiKeys,
}) {
  // `onPick(index, { via })`: `via` is "label" (a labelled stop, which carries the data-ui)
  // or "drag" (the track), so the caller's telemetry can name the control that was used.
  //
  // COMMIT ON RELEASE, not per notch: `onPick` writes through to the server, so a
  // pick per pointer-move is one save per notch crossed. During a drag the knob
  // follows a LOCAL index, so it still tracks the cursor 1:1.
  //
  // The ref is what `endDrag` reads (a state value there would be a stale closure);
  // the state is what re-renders the knob.
  const pendingRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  // Knob (and fill) snap to CELL CENTERS — (i+0.5)/n — so the circle lands under
  // the hash mark / word for each option, not at the edge-to-edge idx/(n-1)
  // stops. The track uses the same 1px side margins as the ticks/labels rows so
  // these percentages share one coordinate space and line up exactly.
  const shownIdx = dragIdx ?? idx;
  const pct = opts.length > 0 ? ((shownIdx + 0.5) / opts.length) * 100 : 0;
  const { trackRef, onPointerDown, onPointerMove, endDrag, trans } = useDragSlider(
    (clientX, rect) => {
      if (opts.length <= 1) return;
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      // Which cell the pointer is over (floor into n equal cells).
      const newIdx = Math.min(opts.length - 1, Math.floor(ratio * opts.length));
      pendingRef.current = newIdx;
      setDragIdx(newIdx);
    },
    () => {
      const picked = pendingRef.current;
      pendingRef.current = null;
      setDragIdx(null);
      // Only when it moved: a press-and-release on the current notch is not a change,
      // and writing it would bump a revision for nothing.
      if (picked != null && picked !== idx) onPick(picked, { via: "drag" });
    },
  );
  return (
    <div>
      {/* telemetry-gesture: dragging commits through onPick, whose caller records the intent */}
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{
          height: 14,
          position: "relative",
          // 1px side margins so the track shares the ticks/labels coordinate space
          // and the cell-centered knob lines up with the hash marks over each word.
          margin: "6px 1px 9px",
          cursor: "pointer",
          touchAction: "none",
          userSelect: "none",
        }}
      >
        {/* Visible track + fill — wrapped in a pointerEvents:none layer so
          the gesture handlers above own all hit-testing. */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            height: 4,
            marginTop: -2,
            borderRadius: 2,
            background: C.border,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${pct}%`,
              background: fill,
              borderRadius: 2,
              transition: trans,
            }}
          />
        </div>
        {/* Knob */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            width: 15,
            height: 15,
            borderRadius: "50%",
            background: "#fff",
            transform: "translate(-50%,-50%)",
            boxShadow: "0 1px 5px rgba(0,0,0,.55)",
            transition: trans,
            left: `${pct}%`,
            pointerEvents: "none",
          }}
        />
      </div>
      {/* Hash marks centered over each label — same flex cells + horizontal
        margin as the labels row below, so each tick sits directly above (centered
        on) its word. Negative top margin pulls them up onto the track line. */}
      <div style={{ display: "flex", margin: "-4px 1px 3px" }}>
        {opts.map((_o, i) => (
          <div
            key={`tick-${i}`}
            style={{ flex: "1 1 0", display: "flex", justifyContent: "center" }}
          >
            <div
              style={{ width: 1.5, height: 5, borderRadius: 1, background: C.gray, opacity: 0.7 }}
            />
          </div>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          color: C.gray,
          margin: "0 1px",
        }}
      >
        {opts.map((o, i) => (
          <span
            key={i}
            onClick={() => onPick(i, { via: "label" })}
            {...ui(uiId, uiKeys ? uiKeys[i] : i)}
            style={{
              flex: "1 1 0",
              cursor: "pointer",
              padding: "2px 2px",
              textAlign: "center",
              color: i === idx ? accent : undefined,
              fontWeight: i === idx ? "bold" : undefined,
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            {fmt ? fmt(o, i) : o}
          </span>
        ))}
      </div>
    </div>
  );
}

// Specialized alert-threshold slider — 0..100% in 1% steps. Differs from the
// generic TrackingSlider in three ways: (1) mini-tick row at every 1% rendered
// as a repeating CSS background (one DOM element instead of 100), (2) labels
// only at the ends (0 / 50 / 100), highlighted purple when the value is ON
// that snap point, (3) live "±X%" readout in brand purple under the slider so
// the user sees the current threshold without reading the labels.
export function AlertThresholdSlider({
  value,
  onChange,
  onCommit,
  fill = C.purpleDim,
  accent = C.purple,
}) {
  // `onChange` follows the pointer; `onCommit` fires once when the drag (or click) ends.
  const { trackRef, onPointerDown, onPointerMove, endDrag, trans } = useDragSlider(
    (clientX, rect) => {
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const v = Math.round(ratio * 100);
      if (v !== value) onChange(v);
    },
    onCommit,
  );
  return (
    <div>
      {/* Track + knob (same geometry as TrackingSlider) */}
      {/* telemetry-gesture: dragging commits through onCommit, whose caller records the intent */}
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{
          height: 14,
          position: "relative",
          margin: "6px 9px 0",
          cursor: "pointer",
          touchAction: "none",
          userSelect: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            height: 4,
            marginTop: -2,
            borderRadius: 2,
            background: C.border,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${value}%`,
              background: fill,
              borderRadius: 2,
              transition: trans,
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            top: "50%",
            width: 15,
            height: 15,
            borderRadius: "50%",
            background: "#fff",
            transform: "translate(-50%,-50%)",
            boxShadow: "0 1px 5px rgba(0,0,0,.55)",
            transition: trans,
            left: `${value}%`,
            pointerEvents: "none",
          }}
        />
      </div>
      {/* Mini-ticks at every 1% — 100 ticks drawn via one repeating-background
        element. Each tile is 1% of the container's width with a 1px tick on
        its left edge; the remainder is transparent. Net effect: tick at
        0%, 1%, 2% … 99%. */}
      <div
        style={{
          margin: "4px 9px 0",
          height: 4,
          backgroundImage: `linear-gradient(to right, ${C.gray} 1px, transparent 1px)`,
          backgroundSize: "1% 100%",
          opacity: 0.55,
          pointerEvents: "none",
        }}
      />
      {/* Live readout — current threshold under the slider in brand purple */}
      <div
        style={{
          textAlign: "center",
          fontSize: 12,
          color: accent,
          fontWeight: "bold",
          margin: "5px 0 3px",
          letterSpacing: 0,
          userSelect: "none",
        }}
      >
        ±{value}%
      </div>
    </div>
  );
}
